import {IGengineClient} from './i-gengine-client';
import {GengineRoom} from './gengine-room';
import {Subject} from 'rxjs';
import {GengineWorld} from './gengine-world';
import {IGenginePlayer} from './i-gengine-player';
import {IWorldInfo} from './i-world-info';
import {IMessage} from './i-message';
import {TWithPlayers} from './gengine-data-holder';

export class GenginePlayer<
  TRoomData extends TWithPlayers,
  TWorldData extends TWithPlayers
  >
  implements IGenginePlayer
{
  room?: GengineRoom<TRoomData>;
  events = new Subject<string>();
  world?: GengineWorld<TWorldData>;

  constructor(public name: string, public client: IGengineClient) {
    this.client.message.subscribe(this.onMessage.bind(this));
    this.client.status.subscribe(this.onConnStatus.bind(this));
  }

  onConnStatus(status: string): void {
    switch (status) {
      case 'disconnected':
        this.room = null;
        this.world = null;
        break;
    }
  }

  onMessage(message: IMessage): void {
    try {
      switch (message.type) {
        case 'new room master':
          console.log(message);
          this.room.setMode(message.data.player === this.name);
          if (this.room.master) {
            this.events.next('room master');
          }
          break;
        case 'new world master':
          console.log(message);
          this.world.setMode(message.data.player === this.name);
          if (this.world.master) {
            this.events.next('world master');
          }
          break;
        default:
          if (this.room) {
            this.room.onMessage(message);
          }
          if (this.world) {
            this.world.onMessage(message);
          }
      }
    } catch (e) {
      console.error(e);
    }
  }

  async send(msg: IMessage): Promise<any> {
    return await this.client.send(msg);
  }

  async enterGame(game: string): Promise<void> {
    await this.send({type: 'join game', data: game});
    this.events.next('game');
  }

  async getWorlds(): Promise<IWorldInfo[]> {
    const resp = await this.send({type: 'get worlds'});
    return resp as IWorldInfo[];
  }

  async enterWorld(world: string): Promise<void> {
    const resp = await this.send({type: 'join world', data: world});
    this.world = new GengineWorld(this, world);
    this.world.setMode(resp.mode === 'master');
    this.world.onHold(() => this.world.data = resp.data);
    this.world.setPlayers(resp.players);
    this.events.next('joining world');
  }

  async leaveWorld(): Promise<void> {
    if (this.world) {
      await this.world.leave();
    }
    delete this.world;
    this.events.next('leaving world');
  }

  // noinspection JSUnusedGlobalSymbols
  async enterRoom(name: string): Promise<GengineRoom<TRoomData> | undefined> {
    if (this.room?.name === name) {
      throw new Error('rethink joining same room twice');
    }
    const resp = await this.send({type: 'join room', data: name});
    console.log(`entering room ${name} as ${resp.mode}`);
    this.leaveRoom();
    this.room = new GengineRoom(this, name);
    this.room.setMode(resp.mode === 'master');
    this.room.onHold(() => this.room.data = resp.data);
    this.room.setPlayers(resp.players);
    this.events.next('joining room');
    return this.room;
  }

  leaveRoom(): void {
    if (!this.room) { return; }
    this.events.next('leaving room');
    try {
      console.log(`leaving room ${this.room.name}`);
      this.room.leave();
    } catch (e) {
      console.log(e);
    }
    delete this.room;
  }
  async update(path: string, value: any): Promise<void> {
    await this.send({type: 'update', data: {
        path, value, room: this.room.name}});
  }

  // noinspection JSUnusedGlobalSymbols
  /** @deprecated use update world instead */
  async startGame(data: any): Promise<void> {
    await this.send({type: 'start game', data});
  }

}
